import { Building2, GraduationCap, MoreHorizontal, Plus, Search, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { apiUrl } from '@/config/api';

const API_BASE = apiUrl('/api/offerings');

interface ExamOffering {
  id: string;
  capacity: number;
  interviewRequired: boolean;
  schedule?: {
    name: string;
  };
  structure?: {
    definitions?: {
      nameFormal: string;
    }[];
  };
  fee?: {
    amount: number;
  };
}

export default function ExamOfferingList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [offerings, setOfferings] = useState<ExamOffering[]>([]);

  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        const res = await fetch(API_BASE);
        if (res.ok) {
          const data = (await res.json()) as ExamOffering[];
          setOfferings(data);
        }
      } catch (error) {
        console.error('Failed to fetch offerings', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOfferings();
  }, []);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Header - Removed for Tab embedding */}
      <div className="flex justify-end mb-4">
        <Button
          className="bg-blue-600 hover:bg-blue-700 shadow-md"
          onClick={() => navigate('/admin/master/offerings/new')}
        >
          <Plus className="w-4 h-4 mr-2" /> 新規募集枠追加
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input placeholder="入試区分・学科名で検索..." className="pl-9" />
        </div>
      </div>

      {/* Offering Table */}
      <Card className="border-t-4 border-t-blue-500 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>入試日程</TableHead>
                <TableHead>募集組織 (学部・学科)</TableHead>
                <TableHead>定員</TableHead>
                <TableHead>面接</TableHead>
                <TableHead>検定料</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    読み込み中...
                  </TableCell>
                </TableRow>
              )}
              {!loading && offerings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                    募集枠が登録されていません。
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                offerings.map((offering) => (
                  <TableRow key={offering.id} className="group hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{offering.schedule?.name || 'Unknown Exam'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span>{offering.structure?.definitions?.[0]?.nameFormal || 'Unknown Dept'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        <Users className="w-4 h-4 text-gray-400" />
                        {offering.capacity} 名
                      </div>
                    </TableCell>
                    <TableCell>
                      {offering.interviewRequired ? (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                          あり
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {offering.fee ? (
                          `¥${offering.fee.amount.toLocaleString()}`
                        ) : (
                          <span className="text-gray-400">設定なし</span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => navigate(`/admin/master/offerings/${offering.id}`)}>
                            編集
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600">削除</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
